import { Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { IGlobalState } from '~reducer';
import Layout from './Layout';
import { currencyFormatter } from '~utilities/formatters';
import { Dispatch } from 'redux';
import { IDictionary } from '~utilities/IDictionary';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import { abaTransactionsSelector } from '~WarehouseLoanTransactionAbas/selectors';
import { abaTransactionsListAction } from '~WarehouseLoanTransactionAbas/actions';

interface IGroupedTransaction {
    allSelected?: boolean;
    amountPrincipal: number;
    children?: IGroupedTransaction[];
    loanCode: string;
    loanUuid: string;
    someSelected?: boolean;
    transactionTime: string;
    uuid: string;
}

export default function Transactions(): ReactElement {
    const { warehouseLoanTransactionAbaUuid } = useParams<{ warehouseLoanTransactionAbaUuid: string }>();

    const transactions: IDictionary<IWarehouseLoanTransaction> = useSelector((state: IGlobalState): IDictionary<IWarehouseLoanTransaction> => abaTransactionsSelector(state, warehouseLoanTransactionAbaUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!transactions) {
            dispatch(abaTransactionsListAction(warehouseLoanTransactionAbaUuid));
        }
    }, [
        dispatch,
        warehouseLoanTransactionAbaUuid,
        transactions,
    ]);

    const summary: (data: readonly IGroupedTransaction[]) => JSX.Element = useCallback((data: readonly IGroupedTransaction[]): JSX.Element => {
        const totalPrincipal: number = _.reduce(data, (totalInterest: number, transaction: IGroupedTransaction): number => totalInterest + transaction.amountPrincipal, 0);

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                <Table.Summary.Cell index={1}>&nbsp;</Table.Summary.Cell>
                <Table.Summary.Cell index={2}>{currencyFormatter.format(totalPrincipal)}</Table.Summary.Cell>
            </Table.Summary.Row>
        );
    }, []);

    if (!transactions) {
        return (
            <Layout
                pageTitle='Transactions'
                section='transactions'
                uuid={warehouseLoanTransactionAbaUuid}
            >
                <Typography.Title level={2}>Transactions</Typography.Title>
                <Spin/>
            </Layout>
        );
    }

    const columns: ColumnsType<IGroupedTransaction> = [
        {
            dataIndex: 'loanCode',
            render: (loanCode: string, row: IGroupedTransaction): ReactNode => <Link to={`/loans/${row.loanUuid}`}>{loanCode}</Link>,
            title: 'Loan',
        },
        {
            dataIndex: 'transactionTime',
            defaultSortOrder: 'descend',
            render: (transactionTime: string, row: IGroupedTransaction): ReactNode => !row.children && transactionTime && dayjs(transactionTime).format('DD/MM/YYYY'),
            sorter: (a: IGroupedTransaction, b: IGroupedTransaction): number => dayjs(a.transactionTime) > dayjs(b.transactionTime) ? 1 : -1,
            title: 'Date',
            width: 220,
        },
        {
            dataIndex: 'amountPrincipal',
            render: (amountPrincipal: number, row: IGroupedTransaction): ReactNode => (
                <Space>
                    <>{currencyFormatter.format(amountPrincipal)}</>
                    {row.children && <Tag>&sum;</Tag>}
                </Space>
            ),
            title: 'Principal',
            width: 200,
        },
    ];

    const groupedTransactions: IDictionary<IGroupedTransaction> = {};

    // Group all items
    _.each(transactions, (unallocatedTransaction: IWarehouseLoanTransaction): void => {
        if (!groupedTransactions[unallocatedTransaction.loanCode]) {
            groupedTransactions[unallocatedTransaction.loanCode] = {
                amountPrincipal: 0,
                children: [],
                loanCode: unallocatedTransaction.loanCode,
                loanUuid: unallocatedTransaction.loanUuid,
                transactionTime: unallocatedTransaction.transactionTime,
                uuid: unallocatedTransaction.loanCode,
            };
        }

        groupedTransactions[unallocatedTransaction.loanCode].children.push(unallocatedTransaction);
        groupedTransactions[unallocatedTransaction.loanCode].amountPrincipal += unallocatedTransaction.amountPrincipal;
    });

    _.each(groupedTransactions, (groupedTransaction: IGroupedTransaction, loanCode: string): void => {
        if (groupedTransaction.children.length > 1) {
            return;
        }

        // Degroup items with 1 child
        groupedTransactions[loanCode] = groupedTransaction.children[0];
    });

    return (
        <Layout
            pageTitle='Transactions'
            section='transactions'
            uuid={warehouseLoanTransactionAbaUuid}
        >
            <Typography.Title level={2}>Transactions</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(groupedTransactions)}
                pagination={false}
                rowKey='loanTransactionUuid'
                size='middle'
                summary={summary}
            />
        </Layout>
    );
}